import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  Link,
  Box,
  Alert,
} from '@mui/material'
import { differenceInYears, toDate } from 'date-fns'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import config from '../../config'
import useConfig from '../../data/use-config'
import useSnackbar from '../../data/use-snackbar'
import {
  activeStepState,
  formDataState,
  loadingState,
  isExpiredDate,
  isGlobalDomain,
} from '../helpers'
import Layout from '../layout/layout.component'
import Ekyc from './ekyc.component'
import { classes, styles } from './use-styles'
import infoLogService from '../../services/info.service'
import customerService from '../../services/customer.service'
import singpassService from '../../services/singpass.service'
import { v4 as uuid } from 'uuid'
import { useLocation, useNavigate } from 'react-router-dom'
import SingpassError from '../layout/singpasserror.component'
import ConfigService from '../../services/config.service'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { validateNRIC } from '../../data/nric-validation'

const StyledDiv = styled('div')(styles)
const WORKPASS_TYPE_ID = 252

function CustomerScan({ step, handleBack, ekyc, prefix }) {
  const { data: idTypes, status } = useConfig('id_type2', true)
  const { data: countries } = useConfig('ctry', true)
  const setActiveStep = useSetRecoilState(activeStepState)
  const setLoading = useSetRecoilState(loadingState)
  const [idType, setIdType] = useState({})
  const [nationality, setNationality] = useState({})
  const [isScanValid, setScanValid] = useState(false)
  const [ekycIdType, setEkycIdType] = useState()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useRecoilState(formDataState)
  const { enqueueSnackbar } = useSnackbar()
  const uniqueId = uuid().slice(0, 8)
  const [myInfoState] = useState(`SELFREG-${uniqueId}`)
  const location = useLocation()
  const navigate = useNavigate()
  const [isSingpassError, setIsSingpassError] = useState(false)
  const [enabledSingpass, setEnabledSingpass] = useState(true)
  const [loadingEnv, setLoadingEnv] = useState(true)
  const lastPath = sessionStorage.getItem('lastPath')
  const idTypesFiltered = useMemo(() => {
    if (formData && formData.isDataOnlyPlan !== true) {
      return idTypes.filter((idType) => idType.value5 !== 'PASSPORT')
    }

    return idTypes
  }, [idTypes, formData])

  const validationSchema = yup.object().shape({
    idType: yup.string().default(''),
    documentNo: yup
      .string()
      .required('Document number is required')
      .when('idType', {
        is: 'NRIC',
        then: (schema) =>
          schema.test('is-nric', '${value} is not valid NRIC', (value) =>
            validateNRIC(value)
          ),
        otherwise: (schema) =>
          schema.when('idType', {
            is: 'WORKP',
            then: (schema) =>
              schema.test('is-fin', '${value} is not valid FIN', (value) =>
                validateNRIC(value, false)
              ),
            otherwise: (schema) => schema,
          }),
      }),
    address: yup
      .string()
      .nullable()
      .max(100, 'Address must be at most 100 characters'),
  })

  const {
    control,
    formState: { errors, isValid },
    getValues,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      idType: '',
      documentNo: formData.documentNo || '',
      address: formData.address || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const validate = useCallback(
    (idType, nationality) => {
      let valid = true
      valid = idType && idType.value1 ? valid : false

      if (
        valid &&
        idType &&
        (idType.value3 === 'CTRY' || idType.value5 === 'WORKP')
      ) {
        valid = nationality && nationality.value1 ? valid : false
      }

      if (ekyc) {
        if (valid) {
          setEkycIdType(
            idType && idType.value3 === 'CTRY'
              ? nationality.value3
              : idType.value3
          )
        } else {
          setEkycIdType()
        }
      }

      setScanValid(valid)
    },
    [ekyc]
  )

  //validate once state have changed, make sure the id type / country have fill before proceed
  useEffect(() => {
    validate(idType, nationality)
  }, [idType, nationality, setNationality, validate])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleWorkpassCheckError = (type) => {
    switch (type) {
      case 'ObjectNotFound':
        return enqueueSnackbar(
          'Your document has expired.  Please re-submit with a valid document.'
        )
      case 'AccessDenied':
        return enqueueSnackbar(
          'FIN Expiry Check: AccessDenied. Please try again later'
        )
      case 'ServiceUnavailable':
        return enqueueSnackbar(
          'FIN Expiry Check: ServiceUnavailable. Please try again later'
        )
      case 'ServerError':
        return enqueueSnackbar(
          'FIN Expiry Check: ServerError. Please try again later'
        )
      case 'ServerBusy':
        return enqueueSnackbar(
          'FIN Expiry Check: ServerBusy. Please try again later'
        )
      default:
        return enqueueSnackbar(
          'FIN Expiry Check : General Error. Please try again later'
        )
    }
  }

  const handleVideoIdCompleted = async (data) => {
    if (
      data.document.expiryDateTS &&
      isExpiredDate(toDate(data.document.expiryDateTS))
    ) {
      setOpen(false)
      return enqueueSnackbar('Document is expired.')
    }

    let idNo =
      idType.value5 === 'PASSPORT'
        ? data.document.passportNumber
        : data.document.documentNumber

    if (!data.document.expiryDateTS && ekycIdType == WORKPASS_TYPE_ID) {
      try {
        const workPassResult = await customerService.workpassCheck(
          data.document.documentNumber
        )
        if (workPassResult) {
          if (workPassResult.status == 'Ok') {
            const validStatus = workPassResult.workPass.some(
              (pass) =>
                pass.passStatus === 'Live' || pass.passStatus === 'Approved'
            )
            if (!validStatus) {
              setOpen(false)
              return handleWorkpassCheckError('ObjectNotFound')
            }
          } else {
            setOpen(false)
            return handleWorkpassCheckError(workPassResult.status)
          }
        }
      } catch (err) {
        setOpen(false)
        return handleWorkpassCheckError('FAIL')
      }
    }

    const name =
      (data.document.subject.primaryName
        ? data.document.subject.primaryName
        : '') +
      (data.document.subject.secondaryName
        ? ' ' + data.document.subject.secondaryName
        : '')

    const dob = toDate(data.document.subject.birthDateTS)
    const idNoInput = getValues('documentNo').toUpperCase()
    let reviseNationality = _.cloneDeep(nationality)

    if (idType.value5 === 'WORKP') {
      let country = _.find(countries, {
        value1: data.document.subject.nationality,
      })
      if (country) {
        reviseNationality = country
      }
    }
    const tamperedDocument =
      data.securityChecks && data.securityChecks.untamperedDocument
        ? !data.securityChecks.untamperedDocument.passed
        : false
    const tamperedVideo =
      data.securityChecks && data.securityChecks.untamperedVideo
        ? !data.securityChecks.untamperedVideo.passed
        : false

    //log eid response purpose
    try {
      infoLogService.log({
        tag: 'eID Result',
        info: {
          videoID: data.id,
          idType: idType.value5,
          country: nationality.value1,
          eID_ID_Type: ekycIdType,
          eId_nationality: data.document.subject.nationality,
          expiredDate: data.document.expiryDateTS,
          name: name,
          idNo: idNo,
          idNoInput: idNoInput,
          dob: dob,
          securityChecks: JSON.stringify(data.securityChecks),
          redemptionCode: formData.redemptionCode || '',
        },
      })
    } catch (err) {
      console.log(err)
    }

    if (idNo.toUpperCase() != idNoInput) {
      setOpen(false)
      try {
        await customerService.postDocMismatch(
          idType.value5,
          idNo,
          idNoInput,
          name,
          dob,
          reviseNationality?.value1
        )
      } catch (err) {
        infoLogService.log({
          tag: 'document mismatch error',
          error: JSON.stringify(err),
        })
      }
      return enqueueSnackbar(
        'We were unable to verify your document number with your ID. Please try again.'
      )
    }

    if (tamperedDocument || tamperedVideo) {
      setOpen(false)
      try {
        await customerService.postDocTampered(
          idType.value5,
          idNo,
          name,
          dob,
          reviseNationality?.value1
        )
      } catch (err) {
        infoLogService.log({
          tag: 'document mismatch error',
          error: JSON.stringify(err),
        })
      }
      return enqueueSnackbar(
        <>
          We are unable to validate your document. Please refer to our{' '}
          <Link
            href="https://www.m1.com.sg/support/faq/prepaid-faq"
            target="_blank"
            rel="noopener noreferrer"
          >
            FAQs
          </Link>{' '}
          for more information.
        </>
      )
    }

    try {
      let fraudResult = await customerService.validateFraud(
        idType.value5,
        idNo,
        name,
        dob,
        reviseNationality?.value1
      )
      if (fraudResult.isBlacklisted || fraudResult.isProfanity) {
        setOpen(false)
        return enqueueSnackbar(
          'We are unable to process your registration. Please try again.'
        )
      }
    } catch (err) {
      setOpen(false)
      return enqueueSnackbar('Something went wrong. Please try again.')
    }

    let address = ''

    if (idType.value5 === 'NRIC') {
      if (
        data.document &&
        data.document.subject &&
        data.document.subject.address
      ) {
        address = data.document.subject.address.trim().replaceAll('\n', ',')
      }
    } else {
      address = getValues('address')
    }

    handleNext({
      ...formData,
      myInfo: 'NO',
      subType: 'PER',
      idType: idType.value5,
      idTypeLabel: idType.value2,
      nationality: reviseNationality?.value1,
      nationalityLabel: reviseNationality?.value2,
      videoIdType: ekycIdType,
      useEkyc: 2,
      ekyc_id: data.id,
      ekyc_name: name,
      ekyc_idNo: idNo,
      ekyc_dob: dob,
      name: name,
      idNo: idNo,
      dob: dob,
      serviceEligibility: true,
      front:
        'data:image/png;base64,' +
        (idType.value5 === 'PASSPORT'
          ? data.document.scan
          : data.document.front),
      back:
        idType.value5 === 'PASSPORT'
          ? null
          : 'data:image/png;base64,' + data.document.back,
      tamperedDocument: tamperedDocument,
      address: address,
    })
  }

  const handleVideoIdFailed = useCallback(
    (errorId, errorMsg, videoId) => {
      infoLogService.log({
        tag: 'eID Result failed',
        info: {
          redemptionCode: formData.redemptionCode || '',
          errorMsg,
          errorId,
          videoId,
        },
      })
      handleClose()
      return enqueueSnackbar(
        <Alert icon={false} severity="error">
          {errorMsg || 'Something went wrong'}. Please proceed to M1 Shop Bugis
          (11am - 8pm) for assistance if issue persists. <br />
          <br />
          <b>Error ID: </b> {errorId} <br />
          <b>Video ID: </b> {videoId} <br />
        </Alert>
      )
    },
    [enqueueSnackbar, handleClose, formData.redemptionCode]
  )

  const handleVideoIdTimeout = useCallback(() => {
    enqueueSnackbar(
      'Unable to connect. Please check your connection and try again.'
    )
  }, [enqueueSnackbar])

  const handleVideoIdError = useCallback(() => {
    enqueueSnackbar(
      'Unable to connect. Please check your connection and try again.'
    )
  }, [enqueueSnackbar])

  const handleScan = async () => {
    infoLogService.log({
      tag: 'eID Before Start',
      info: { country: nationality.value1, eID_ID_Type: ekycIdType },
    })

    if (config.NODE_ENV === 'development') {
      // test data
      handleVideoIdCompleted({
        id: 'dfbc90e4-a97a-496c-b3b8-5900a82fa9a4',
        document: {
          subject: {
            primaryName: 'asddd',
            secondaryName: 'name',
            birthDateTS: new Date('1994-01-01').getTime(),
            address: 'APT BLK 445A XXXA AVENUE в\n#16-00$ \nSINGAPORE 123456',
          },
          passportNumber: 'abc123',
          documentNumber: 'S2380240F',
          scan: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBg+A8AAQQBAHAgZQsAAAAASUVORK5CYII=',
          front:
            'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBg+A8AAQQBAHAgZQsAAAAASUVORK5CYII=',
          back: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBg+A8AAQQBAHAgZQsAAAAASUVORK5CYII=',
        },
        securityChecks: {
          untamperedDocument: {
            name: 'Untampered Document',
            passed: false,
          },
          untamperedVideo: {
            name: 'Untampered Video',
            passed: true,
          },
        },
      })
    } else {
      setOpen(true)
    }
  }

  const handleNext = (data) => {
    let age = differenceInYears(Date.now(), data.dob)
    let ageEligibility = age >= 15 && age <= 120
    data = { ...data, ageEligibility: ageEligibility }
    setFormData({
      ...formData,
      ..._.mapKeys(data, (v, key) => {
        return prefix + key
      }),
    })

    setActiveStep((prevActiveStep) => prevActiveStep + 2)
  }

  const handleIdTypeChange = (e) => {
    setValue('address', '')
    if (e.target.value !== '') {
      const selectedType = idTypes[e.target.value]
      setValue('idType', selectedType.value5)
      setIdType(selectedType)
      if (selectedType.value5 === 'NRIC') {
        setNationality(_.find(countries, { value1: 'SGP' }))
      } else {
        setNationality({})
      }
    } else {
      setIdType({})
      setScanValid(false)
    }
  }

  const handleNationalityChange = (e, newValue) => {
    setNationality(newValue)
  }

  const getPersonDataFromSingpass = async (authCode, state) => {
    try {
      setLoading(true)
      const result = await singpassService.handleCallback(
        authCode,
        state,
        isGlobalDomain
      )
      if (result) {
        const fraudResult = await customerService.validateFraud(
          result.idType,
          result.idNo,
          result.name,
          result.dob,
          result.nationality
        )

        if (fraudResult.isBlacklisted || fraudResult.isProfanity) {
          enqueueSnackbar(
            'We are unable to process your registration. Please try again.'
          )
          return
        }

        handleNext({
          myInfo: 'YES',
          subType: 'PER',
          name: result.name,
          idType: result.idType,
          idTypeLabel: result.idTypeLabel,
          nationalityLabel: result.nationalityLabel,
          nationality: result.nationality,
          dob: result.dob ? new Date(result.dob) : '',
          idNo: result.idNo,
          txnNo: result.txnNo,
          useEkyc: 0,
          address: result.address,
          msisdn: sessionStorage.getItem('msisdn'),
          iccid: sessionStorage.getItem('iccid'),
          email: sessionStorage.getItem('email'),
          redemptionCode: sessionStorage.getItem('redemptionCode'),
          isDataOnlyPlan: sessionStorage.getItem('isDataOnlyPlan'),
        })
      }
    } catch (err) {
      setIsSingpassError(true)
      //reassign msisdn and iccid back to formData if singpass error to prevent empty msisdn and iccid happen
      setFormData({
        ...formData,
        msisdn: sessionStorage.getItem('msisdn'),
        iccid: sessionStorage.getItem('iccid'),
        email: sessionStorage.getItem('email'),
        redemptionCode: sessionStorage.getItem('redemptionCode'),
      })
    } finally {
      setLoading(false)
      navigate(lastPath, { replace: true })
    }
  }

  //get singpass redirect url
  const handleSingpass = async () => {
    setLoading(true)
    try {
      sessionStorage.setItem('lastPath', location.pathname)
      if (formData.redemptionCode) {
        sessionStorage.setItem('redemptionCode', formData.redemptionCode)
      }

      if (formData.msisdn) {
        sessionStorage.setItem('msisdn', formData.msisdn)
      }

      if (formData.iccid) {
        sessionStorage.setItem('iccid', formData.iccid)
      }

      if (formData.email) {
        sessionStorage.setItem('email', formData.email)
      }

      if (formData.isDataOnlyPlan) {
        sessionStorage.setItem('isDataOnlyPlan', formData.isDataOnlyPlan)
      }
      const result = await singpassService.getAuthorizationUrl(
        myInfoState,
        isGlobalDomain
      )
      if (result) {
        window.location.href = result.authorizationUrl
      }
    } catch (err) {
      setIsSingpassError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const getEnv = async () => {
      const { enabledSingpass } = await ConfigService.getEnv()
      setEnabledSingpass(enabledSingpass)
      setLoadingEnv(false)
    }

    try {
      getEnv()
    } catch (err) {
      setLoadingEnv(false)
      console.log(err)
    }
  }, [])

  //handle Singpass callback and get person data
  useEffect(() => {
    //handle if singpass error
    if (location.state?.error) {
      if (location.state?.error === 'access_denied') {
        navigate(lastPath, { replace: true })
        return enqueueSnackbar(location.state?.errorDesc)
      }
      navigate(lastPath, { replace: true })
      return setIsSingpassError(true)
    }

    if (countries.length > 0) {
      if (location.state?.code) {
        getPersonDataFromSingpass(location.state.code, location.state.state)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries])

  return (
    <>
      <Layout
        skipTitle={isSingpassError ? true : false}
        step={step}
        content={
          <>
            {isSingpassError ? (
              <SingpassError
                handleBack={() => setIsSingpassError(!isSingpassError)}
              />
            ) : (
              <StyledDiv>
                <Grid container columnSpacing={8}>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'top',
                      justifyContent: 'left',
                      height: '150px',
                    }}
                  >
                    <img alt="scan" src={`${config.PUBLIC_URL}/scan_img.png`} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {!loadingEnv && (
                      <>
                        {enabledSingpass ? (
                          <>
                            <Box textAlign={'center'} mt={'20px'} mb={'10px'}>
                              <img
                                src={`${config.PUBLIC_URL}/singpass/singpass_logo_fullcolours.png`}
                                alt="singpass_logo"
                                className={classes.singpassLogo}
                              />
                            </Box>
                            <Typography>
                              Singaporeans, PR or FIN holder can use Myinfo to
                              instantly fill in your personal details.
                              <br />
                              <br />
                              You will be re-directed to Singpass login.
                            </Typography>
                            <Box
                              display={'flex'}
                              alignItems={'center'}
                              justifyContent={'center'}
                            >
                              <Button
                                className={classes.singpassBtn}
                                variant="contained"
                                onClick={handleSingpass}
                              >
                                <Box>Retrieve Myinfo</Box>
                                <Box>
                                  with{' '}
                                  <img
                                    alt="sg_logo"
                                    src={
                                      config.PUBLIC_URL +
                                      '/singpass/singpass_logo_white.svg'
                                    }
                                  />
                                </Box>
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Typography variant="subtitle2">
                              Please select your ID type.
                            </Typography>
                            <FormControl
                              disabled={status !== 'fetched'}
                              className={classes.dropdown}
                              variant="outlined"
                              fullWidth
                            >
                              <Select
                                native={isMobile ? true : false}
                                defaultValue=""
                                onChange={handleIdTypeChange}
                                inputProps={{
                                  name: 'idType',
                                  id: 'idType',
                                  'aria-label': 'idType',
                                }}
                              >
                                {isMobile ? (
                                  <option aria-label="None" value="" />
                                ) : (
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>
                                )}
                                {idTypesFiltered &&
                                  idTypesFiltered.map((idType, index) => {
                                    return isMobile ? (
                                      <option key={idType.id} value={index}>
                                        {idType.value2}
                                      </option>
                                    ) : (
                                      <MenuItem key={idType.id} value={index}>
                                        {idType.value2}
                                      </MenuItem>
                                    )
                                  })}
                              </Select>
                            </FormControl>
                            {idType &&
                              (idType.value3 === 'CTRY' ||
                                idType.value5 === 'WORKP') && (
                                <>
                                  <Typography variant="subtitle2">
                                    Select your nationality.
                                  </Typography>
                                  <Autocomplete
                                    key={idType.value1} //set key to re-render if IDtype option change
                                    options={
                                      idType.value5 === 'PASSPORT'
                                        ? _.filter(
                                            countries,
                                            ({ value1, value3 }) =>
                                              value1 !== 'SGP' && value3 !== ''
                                          )
                                        : countries
                                    }
                                    getOptionLabel={(option) => option.value2}
                                    onChange={handleNationalityChange}
                                    loading
                                    renderInput={(params) => (
                                      <TextField
                                        className={classes.dropdown}
                                        {...params}
                                        margin="normal"
                                        placeholder="Find a country"
                                      />
                                    )}
                                  />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontSize: {
                                        xs: '0.75rem',
                                        sm: '0.85rem',
                                      },
                                      mb: '20px',
                                    }}
                                  >
                                    If you are unable to locate your
                                    nationality, please refer to our{' '}
                                    <Link
                                      componenet="button"
                                      target="_blank"
                                      rel="noopener"
                                      href="https://www.m1.com.sg/Support/FAQ/prepaid-faq"
                                      sx={{
                                        fontSize: {
                                          xs: '0.75rem',
                                          sm: '0.85rem',
                                        },
                                      }}
                                    >
                                      <b>FAQ section</b>
                                    </Link>{' '}
                                    for more info.
                                  </Typography>
                                </>
                              )}
                            {idType && idType.value3 && (
                              <Box>
                                <Typography variant="subtitle2">
                                  Document Number
                                </Typography>
                                <Controller
                                  control={control}
                                  name="documentNo"
                                  defaultValue={formData.documentNo || ''}
                                  render={({ field }) => (
                                    <TextField
                                      className={classes.dropdown}
                                      {...field}
                                      fullWidth
                                      margin="normal"
                                      variant="outlined"
                                      placeholder="Document No."
                                      name="documentNo"
                                      id="documentNo"
                                      autoComplete="off"
                                      error={errors.documentNo ? true : false}
                                      helperText={errors.documentNo?.message}
                                    />
                                  )}
                                />
                              </Box>
                            )}
                            {idType &&
                              idType.value5 &&
                              idType.value5 !== 'NRIC' && (
                                <Box>
                                  <Typography variant="subtitle2">
                                    Address
                                  </Typography>
                                  <Controller
                                    control={control}
                                    name="address"
                                    defaultValue={formData.address || ''}
                                    render={({ field }) => (
                                      <TextField
                                        className={classes.dropdown}
                                        {...field}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        autoComplete="off"
                                        placeholder="Full Address"
                                        name="address"
                                        id="address"
                                        error={errors.address ? true : false}
                                        helperText={errors.address?.message}
                                      />
                                    )}
                                  />
                                </Box>
                              )}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2">
                                Please have your personal ID ready for the next
                                section.
                              </Typography>
                            </Grid>
                            <Box
                              display={'flex'}
                              alignItems={'center'}
                              justifyContent={'center'}
                            >
                              <Grid item xs={6}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  disabled={!(isValid && isScanValid)}
                                  onClick={handleScan}
                                  fullWidth
                                >
                                  Upload Document
                                </Button>
                              </Grid>
                            </Box>
                          </>
                        )}
                      </>
                    )}
                    <Box
                      display={'flex'}
                      alignItems={'center'}
                      justifyContent={'center'}
                    >
                      <Grid item xs={6}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={handleBack}
                        >
                          Back
                        </Button>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </StyledDiv>
            )}
          </>
        }
      />
      {ekycIdType && open && (
        <Ekyc
          open={open}
          onClose={handleClose}
          idType={ekycIdType}
          onCompleted={handleVideoIdCompleted}
          onError={handleVideoIdError}
          onTimeout={handleVideoIdTimeout}
          onFailed={handleVideoIdFailed}
        />
      )}
    </>
  )
}

CustomerScan.defaultProps = {
  ekyc: true,
  prefix: '',
}

CustomerScan.propTypes = {
  step: PropTypes.object.isRequired,
  handleBack: PropTypes.func.isRequired,
  ekyc: PropTypes.bool,
  prefix: PropTypes.string,
}

export default CustomerScan
